import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, alpha, Divider, TablePagination,
  IconButton,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area,
} from 'recharts';
import { DashboardStats } from '../services/api';

interface DashboardChartsProps {
  stats: DashboardStats;
}

const PROCESS_COLORS: Record<string, string> = {
  daily_fee: '#3B82F6',
  unloading: '#10B981',
  pallet: '#F59E0B',
  vehicle: '#8B5CF6',
};

const PROCESS_LABELS: Record<string, string> = {
  daily_fee: 'Structured Data',
  unloading: 'Semi-Structured Data',
  pallet: 'Unstructured Data',
  vehicle: 'Image & Multimedia Data',
};

const STATUS_COLORS: Record<string, string> = {
  approved: '#10B981',
  processed: '#3B82F6',
  pending: '#F59E0B',
  unknown: '#64748B',
  rejected: '#EF4444',
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4'];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: 2,
        p: 1.5,
        backdropFilter: 'blur(8px)',
      }}>
        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 500 }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const DashboardCharts: React.FC<DashboardChartsProps> = ({ stats }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const gridColor = isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.15)';
  const textColor = isDark ? '#94A3B8' : '#64748B';

  // Pagination state for Per-Document Token Usage table
  const [tokenPage, setTokenPage] = useState(0);
  const ROWS_PER_PAGE = 10;

  const totalTokens = stats.token_usage?.total_tokens || 0;
  const totalCost = stats.token_usage?.total_cost_usd || 0;
  const totalRecords = stats.token_usage?.records?.length || 0;
  const avgCostPerCall = totalRecords > 0 ? totalCost / totalRecords : 0;

  // Per-process breakdown data
  const processBreakdown = Object.entries(stats.token_usage?.by_process || {})
    .map(([key, val]) => ({
      process_type: key,
      label: PROCESS_LABELS[key] || key,
      color: PROCESS_COLORS[key] || '#64748B',
      tokens: val.tokens,
      cost: val.cost,
      docs: val.docs,
      costPercent: totalCost > 0 ? (val.cost / totalCost) * 100 : 0,
    }))
    .sort((a, b) => b.cost - a.cost);

  // Token per-document records for histogram
  const tokenPerDoc = (stats.token_usage?.records || []).slice(0, 20).map(r => {
    const rawName = r.filename || (r.document_id || 'unknown').slice(0, 10);
    // Strip extension and truncate for clean label
    const cleanName = rawName.replace(/\.(pdf|jpg|jpeg|png|tiff|docx?)$/i, '').slice(0, 25);
    return {
      doc: cleanName,
      prompt: r.prompt_tokens,
      completion: r.completion_tokens,
      total: r.total_tokens,
      cost: Number(r.cost_usd.toFixed(4)),
      process: r.process_type,
      fill: PROCESS_COLORS[r.process_type] || '#3B82F6',
    };
  });

  // Process distribution for pie chart
  const processDistribution = [
    { name: 'Structured', value: stats.process_counts.daily_fee, color: PROCESS_COLORS.daily_fee },
    { name: 'Semi-Structured', value: stats.process_counts.unloading, color: PROCESS_COLORS.unloading },
    { name: 'Unstructured', value: stats.process_counts.pallet, color: PROCESS_COLORS.pallet },
    { name: 'Image & Multimedia', value: stats.process_counts.vehicle, color: PROCESS_COLORS.vehicle },
  ].filter(d => d.value > 0);

  // OCR text volume data
  const tokenData = stats.charts.token_histogram.map(item => ({
    ...item,
    fill: PROCESS_COLORS[item.process_type] || '#3B82F6',
  }));

  // Upload timeline
  const timelineData = stats.charts.upload_timeline.map(item => ({
    ...item,
    label: new Date(item.month + '-01').toLocaleDateString('en', { month: 'short', year: '2-digit' }),
  }));

  // Status distribution
  const statusDistribution = Object.entries(stats.status_counts)
    .filter(([_, v]) => v > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: STATUS_COLORS[status] || '#64748B',
    }));

  return (
    <Grid container spacing={3}>

      {/* ====== SECTION: Gemini Cost Analytics ====== */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>
            💰
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Gemini Cost Analytics
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Real-time token → dollar tracking · Gemini 2.5 Flash
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* Summary Cards Row */}
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{
          position: 'relative', overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #F59E0B, #D97706)' },
        }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
                  TOTAL SPEND
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                  ${totalCost.toFixed(4)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {totalRecords} LLM calls · via Gemini
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: alpha('#F59E0B', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                💵
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{
          position: 'relative', overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #3B82F6, #1D4ED8)' },
        }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
                  TOTAL TOKENS
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                  {formatTokens(totalTokens)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  prompt + completion
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: alpha('#3B82F6', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                🔤
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{
          position: 'relative', overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #10B981, #059669)' },
        }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
                  AVG COST / CALL
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                  ${avgCostPerCall.toFixed(4)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {totalRecords > 0 ? formatTokens(Math.round(totalTokens / totalRecords)) : '0'} tokens/call
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: alpha('#10B981', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                📊
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{
          position: 'relative', overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #8B5CF6, #7C3AED)' },
        }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1 }}>
                  GEMINI MODELS
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                  1
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  gemini-2.5-flash
                </Typography>
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: alpha('#8B5CF6', 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                🤖
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Per-Process Cost Breakdown Table */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18, lineHeight: 1 }}>⚙️</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Per-Process Cost Breakdown
              </Typography>
              <Chip label={`${processBreakdown.length} processes`} size="small" sx={{ ml: 'auto' }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>PROCESS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>OCR COST</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#10B981', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>TOKENS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#8B5CF6', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>TOTAL COST</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1, minWidth: 200 }}>COST DISTRIBUTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processBreakdown.map((row) => (
                    <TableRow key={row.process_type} sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: alpha(row.color, 0.15),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: row.color, fontWeight: 700, fontSize: 14,
                          }}>
                            {row.label[0]}
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.label}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {formatTokens(row.tokens)} tokens · {row.docs} docs
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#F59E0B' }}>
                          ${row.cost.toFixed(4)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#10B981' }}>
                          {formatTokens(row.tokens)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#8B5CF6' }}>
                          ${row.cost.toFixed(4)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={row.costPercent}
                            sx={{
                              flex: 1, height: 10, borderRadius: 5,
                              backgroundColor: alpha(row.color, 0.1),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 5,
                                background: `linear-gradient(90deg, ${row.color} 0%, ${alpha(row.color, 0.7)} 100%)`,
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 35 }}>
                            {row.costPercent.toFixed(0)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {processBreakdown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No token usage data yet. Process a document to see LLM cost tracking.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Token Consumption Histogram + Cost by Model */}
      {tokenPerDoc.length > 0 && (
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 18, lineHeight: 1 }}>✨</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Token Consumption by Process
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`LIVE USAGE`} size="small" sx={{ backgroundColor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 700, fontSize: '0.65rem' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  TOTAL <strong style={{ marginLeft: 4, color: isDark ? '#fff' : '#000' }}>{formatTokens(totalTokens)}</strong>
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                  EST. COST <strong style={{ marginLeft: 4, color: isDark ? '#fff' : '#000' }}>${totalCost.toFixed(4)}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {processBreakdown.map(p => (
                  <Box key={p.process_type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {p.label.split(' ')[0]} <strong style={{ color: p.color }}>${p.cost.toFixed(4)}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tokenPerDoc} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis dataKey="doc" tick={{ fontSize: 9, fill: textColor }} angle={-35} textAnchor="end" interval={0} height={70} />
                    <YAxis tick={{ fontSize: 11, fill: textColor }} label={{ value: 'Tokens', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: textColor } }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="top" height={30} formatter={(value: string) => <span style={{ color: textColor, fontSize: 12, fontWeight: 500 }}>{value}</span>} />
                    <Bar dataKey="prompt" name="Input Tokens" stackId="a" fill="#3B82F6" />
                    <Bar dataKey="completion" name="Output Tokens" stackId="a" fill="#8B5CF6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Cost by Model */}
      <Grid item xs={12} lg={tokenPerDoc.length > 0 ? 4 : 12}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 18, lineHeight: 1 }}>🪙</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Cost by Model
              </Typography>
              <Chip label="1 model" size="small" sx={{ ml: 'auto' }} variant="outlined" />
            </Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3B82F6' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>gemini-2.5-flash</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>${totalCost.toFixed(4)}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {totalRecords} calls · {formatTokens(totalTokens)} tokens
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={100}
                  sx={{
                    mt: 1, height: 8, borderRadius: 4,
                    backgroundColor: alpha('#3B82F6', 0.1),
                    '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(90deg, #3B82F6, #1D4ED8)' },
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, textAlign: 'right' }}>100.0%</Typography>
              </CardContent>
            </Card>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
              Model Pricing
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Input (per 1M tokens)</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>$0.15</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Output (per 1M tokens)</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>$0.60</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Thinking (per 1M tokens)</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>$3.50</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Per-Document Token Usage Detail Table */}
      {(stats.token_usage?.records || []).length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 18, lineHeight: 1 }}>📋</Typography>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Per-Document Token Usage
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Detailed token consumption for each processed document
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={`${(stats.token_usage?.records || []).length} documents`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                      <TableCell>Document</TableCell>
                      <TableCell>Process Type</TableCell>
                      <TableCell align="right">Prompt Tokens</TableCell>
                      <TableCell align="right">Completion Tokens</TableCell>
                      <TableCell align="right">Total Tokens</TableCell>
                      <TableCell align="right">Cost (USD)</TableCell>
                      <TableCell align="right">LLM Calls</TableCell>
                      <TableCell>Processed At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(stats.token_usage?.records || []).slice(tokenPage * ROWS_PER_PAGE, (tokenPage + 1) * ROWS_PER_PAGE).map((record, idx) => {
                      const processLabel = PROCESS_LABELS[record.process_type] || record.process_type;
                      const processColor = PROCESS_COLORS[record.process_type] || '#64748B';
                      const docName = record.filename || record.document_id.slice(0, 12) + '...';
                      const createdAt = record.created_at
                        ? new Date(record.created_at).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                          })
                        : '—';
                      return (
                        <TableRow key={idx} sx={{
                          '&:hover': { backgroundColor: alpha(processColor, 0.04) },
                          '&:last-child td': { borderBottom: 0 },
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{
                                width: 32, height: 32, borderRadius: 1.5,
                                backgroundColor: alpha(processColor, 0.1),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 14, flexShrink: 0,
                              }}>
                                📄
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                                  {docName}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  {record.document_id.slice(0, 8)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={processLabel}
                              size="small"
                              sx={{
                                backgroundColor: alpha(processColor, 0.1),
                                color: processColor,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 24,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#3B82F6' }}>
                              {record.prompt_tokens.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#8B5CF6' }}>
                              {record.completion_tokens.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {record.total_tokens.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                              ${record.cost_usd.toFixed(4)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={record.num_calls || '—'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {createdAt}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals Row */}
                    <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700, pt: 1.5 } }}>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Total ({(stats.token_usage?.records || []).length} documents)
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#3B82F6' }}>
                          {(stats.token_usage?.records || []).reduce((s, r) => s + r.prompt_tokens, 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#8B5CF6' }}>
                          {(stats.token_usage?.records || []).reduce((s, r) => s + r.completion_tokens, 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {totalTokens.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#F59E0B' }}>
                          ${totalCost.toFixed(4)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {totalRecords}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={(stats.token_usage?.records || []).length}
                page={tokenPage}
                onPageChange={(_, newPage) => setTokenPage(newPage)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[10]}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  '& .MuiTablePagination-toolbar': { minHeight: 48 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.8rem', fontWeight: 500,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* ====== SECTION: Document Analytics ====== */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>
            📄
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Document Analytics
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Upload trends, document types, and processing status
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* OCR Text Volume */}
      <Grid item xs={12} lg={8}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              OCR Text Volume per Document
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Estimated text tokens extracted (top 30)
            </Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tokenData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="filename" tick={{ fontSize: 10, fill: textColor }} angle={-45} textAnchor="end" interval={0} height={80} />
                  <YAxis tick={{ fontSize: 11, fill: textColor }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={30} formatter={(value: string) => <span style={{ color: textColor, fontSize: 12, fontWeight: 500 }}>{value}</span>} />
                  <Bar dataKey="tokens" name="OCR Tokens Extracted" radius={[4, 4, 0, 0]}>
                    {tokenData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Process Distribution Pie */}
      <Grid item xs={12} lg={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Process Distribution</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Documents by process type</Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={processDistribution} cx="50%" cy="45%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" stroke="none">
                    {processDistribution.map((entry, index) => (<Cell key={index} fill={entry.color} />))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} formatter={(value: string) => <span style={{ color: textColor, fontSize: 12 }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Upload Timeline */}
      <Grid item xs={12} lg={6}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Upload Timeline</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Documents uploaded over time</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorUploads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: textColor }} />
                  <YAxis tick={{ fontSize: 11, fill: textColor }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={30} formatter={(value: string) => <span style={{ color: textColor, fontSize: 12, fontWeight: 500 }}>{value}</span>} />
                  <Area type="monotone" dataKey="count" name="Documents Uploaded" stroke="#3B82F6" strokeWidth={2} fill="url(#colorUploads)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Status Overview */}
      <Grid item xs={12} lg={6}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Document Status Overview</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Current processing status</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusDistribution} cx="50%" cy="45%" outerRadius={90} paddingAngle={3} dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`} labelLine={{ stroke: textColor }} stroke="none">
                    {statusDistribution.map((entry, index) => (<Cell key={index} fill={entry.color} />))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} formatter={(value: string) => <span style={{ color: textColor, fontSize: 12 }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Status by Process + Doc Formats */}
      <Grid item xs={12} lg={6}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Status by Process Type</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Processing status breakdown per process</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.charts.process_status.map(d => ({ ...d, name: (PROCESS_LABELS[d.process_type] || d.process_type).split(' ')[0] }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: textColor }} />
                  <YAxis tick={{ fontSize: 11, fill: textColor }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend formatter={(value: string) => <span style={{ color: textColor, fontSize: 12 }}>{value}</span>} />
                  <Bar dataKey="approved" name="Approved" stackId="a" fill="#10B981" />
                  <Bar dataKey="processed" name="Processed" stackId="a" fill="#3B82F6" />
                  <Bar dataKey="pending" name="Pending" stackId="a" fill="#F59E0B" />
                  <Bar dataKey="unknown" name="Unknown" stackId="a" fill="#64748B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Document Formats</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>File type distribution</Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.charts.doc_types} layout="vertical" margin={{ top: 5, right: 30, left: 50, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: textColor }} />
                  <YAxis dataKey="type" type="category" tick={{ fontSize: 12, fill: textColor }} width={45} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={30} formatter={(value: string) => <span style={{ color: textColor, fontSize: 12, fontWeight: 500 }}>{value}</span>} />
                  <Bar dataKey="count" name="Document Count" radius={[0, 6, 6, 0]}>
                    {stats.charts.doc_types.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DashboardCharts;
